/*
  Mapper zwischen der bestehenden Scheduler-API und dem V2-Frontend-Modell.
*/

import { tokens } from "../../../theme";
import { formatTime, toIsoDate } from "../domain/date/month";
import { INDIVIDUAL_COLOR, isIndividualEntry } from "../domain/model/departments";
import { formatEmployeeShortName, shortenFullName } from "../domain/model/employeeLabels";
import { normalizePeriodSummary } from "../domain/model/periodSummary";
import { normalizeShiftDisplayMode } from "../domain/model/shiftDisplay";

const TIMETAB_GREEN_ACCENT = tokens("light").greenAccent;
const FALLBACK_SHIFT_COLOR = TIMETAB_GREEN_ACCENT[500];
const WORKLOAD_HOUR_EPSILON = 0.05;

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizeBoolean(value, fallback = false) {
  if (value == null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y", "ja"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "nein"].includes(normalized)) return false;
  return fallback;
}

function normalizeReleaseStatus(value, isReleasedFlag = null, fallback = "draft") {
  const normalized = normalizeText(value).toLowerCase();
  if (["released", "draft", "open"].includes(normalized)) return normalized;
  if (normalizeBoolean(isReleasedFlag, false) || normalizeBoolean(value, false)) return "released";
  if (value === false || value === 0 || ["false", "0"].includes(normalized)) return "draft";
  return normalized || fallback;
}

function parseNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/*
  Wie parseNumber, aber "kein Wert" bleibt null statt 0 zu werden. Bei den
  Stunden pro Tag ist das ein Bedeutungsunterschied: null heisst "kein fester
  Wert", 0 heisst "zählt null Stunden".
*/
function parseOptionalNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatHours(value) {
  const normalizedValue = parseNumber(value);
  const formatter = new Intl.NumberFormat("de-CH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(normalizedValue) ? 0 : 1,
  });
  return `${formatter.format(normalizedValue)}h`;
}

function getProgressState(plannedHours, targetHours) {
  const planned = parseNumber(plannedHours);
  const target = parseNumber(targetHours);

  if (!target || target <= 0) {
    return {
      tone: "neutral",
      progressDegrees: 0,
      overageDegrees: 0,
    };
  }

  const delta = planned - target;
  const ratio = planned / target;

  if (Math.abs(delta) <= WORKLOAD_HOUR_EPSILON) {
    return {
      tone: "complete",
      progressDegrees: 360,
      overageDegrees: 0,
    };
  }

  if (delta < 0) {
    return {
      tone: "normal",
      progressDegrees: Math.max(0, Math.min(360, ratio * 360)),
      overageDegrees: 0,
    };
  }

  return {
    tone: "over",
    progressDegrees: 360,
    overageDegrees: Math.max(0, Math.min(360, (ratio - 1) * 360)),
  };
}

function formatVacationDays(value) {
  const count = Math.max(0, parseNumber(value));
  return count === 1 ? "1 Ferientag" : `${count} Ferientage`;
}

export function parseSchedulerUserRow(value) {
  const [id, groupId, firstName, lastName, email, regularUser, weeklyHours, hasPhone] = String(value || "")
    .split(",")
    .map((part) => part.trim());

  const numericId = parseNumber(id);
  const name = `${normalizeText(firstName)} ${normalizeText(lastName)}`.trim() || normalizeText(email) || "Unbenannt";

  return {
    id: String(id || ""),
    numericId,
    groupId: parseNumber(groupId),
    firstName: normalizeText(firstName),
    lastName: normalizeText(lastName),
    email: normalizeText(email),
    name,
    regularUser: parseNumber(regularUser) === 1,
    weeklyHours: parseNumber(weeklyHours),
    hasPhone: parseNumber(hasPhone) === 1,
    isPlaceholder: numericId < 0,
  };
}

function normalizeTemplateBreak(row) {
  if (!row || typeof row !== "object") return null;
  const type = normalizeText(row.break_type).toLowerCase();
  const startTime = normalizeText(row.start_time);
  const endTime = normalizeText(row.end_time);
  const durationMinutes = row.duration_minutes == null ? null : Number(row.duration_minutes);

  if (type === "fixed" && startTime && endTime) {
    return { type: "fixed", startTime, endTime, durationMinutes: null };
  }
  if (type === "duration" && Number.isFinite(durationMinutes) && durationMinutes > 0) {
    return { type: "duration", startTime: "", endTime: "", durationMinutes };
  }
  return null;
}

export function mapShiftOptions(shiftList) {
  return (Array.isArray(shiftList) ? shiftList : []).reduce((accumulator, item) => {
    const id = String(item?.[0] ?? "").trim();
    if (!id) return accumulator;
    accumulator[id] = {
      id,
      name: normalizeText(item?.[1]),
      timeLabel: normalizeText(item?.[2]),
      description: normalizeText(item?.[3]),
      department: normalizeText(item?.[4]),
      shortName: normalizeText(item?.[5]),
      departmentShortName: normalizeText(item?.[6]),
      // Stammdaten-Pausen der Vorlage: der Editor uebernimmt sie beim Anlegen
      // als Pausen des Eintrags.
      breaks: (Array.isArray(item?.[7]) ? item[7] : []).map(normalizeTemplateBreak).filter(Boolean),
    };
    return accumulator;
  }, {});
}

export function mapShiftTemplates(shiftList, colorMap = {}) {
  return Object.values(mapShiftOptions(shiftList))
    .map((template) => ({
      ...template,
      color: normalizeText(colorMap?.[template.id]) || FALLBACK_SHIFT_COLOR,
    }))
    .sort((left, right) => {
      const departmentCompare = String(left.department || "").localeCompare(String(right.department || ""), "de", {
        sensitivity: "base",
      });
      if (departmentCompare !== 0) return departmentCompare;
      return String(left.name || "").localeCompare(String(right.name || ""), "de", {
        sensitivity: "base",
      });
    });
}

export function normalizeAbsenceOption(row) {
  const id = String(row?.id ?? row?.AbsenceID ?? "").trim();
  const name = normalizeText(row?.name ?? row?.label);
  if (!id || !name) return null;
  const iconKey = normalizeText(row?.icon_key ?? row?.iconKey).toLowerCase();

  return {
    id,
    name,
    label: name,
    color: normalizeText(row?.color_hex ?? row?.colorHex),
    iconKey,
    countsAsWorkTime: normalizeBoolean(row?.counts_as_work_time ?? row?.countsAsWorkTime, true),
    // Kein fester Wert am Typ heisst: es bleibt beim individuellen Tagessoll.
    hoursPerDay: parseOptionalNumber(row?.hours_per_day ?? row?.hoursPerDay),
    isActive: normalizeBoolean(row?.is_active ?? row?.isActive, true),
    isSystemDefault: normalizeBoolean(row?.is_system_default ?? row?.isSystemDefault, false),
    isVacation: normalizeBoolean(row?.is_vacation ?? row?.isVacation, false),
  };
}

export function mapAbsenceOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map(normalizeAbsenceOption)
    .filter(Boolean)
    .sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || ""), "de", { sensitivity: "base" })
    );
}

export function normalizePublicHoliday(row) {
  const date = toIsoDate(row?.date || row?.holiday_date);
  if (!date) return null;

  return {
    id: row?.id == null ? `${date}:${normalizeText(row?.name)}` : String(row.id),
    date,
    name: normalizeText(row?.name) || "Feiertag",
    localName: normalizeText(row?.local_name),
    sourceType: normalizeText(row?.source_type) || "manual",
    locationLabel: normalizeText(row?.location_label),
    countryCode: normalizeText(row?.country_code),
    subdivisionCode: normalizeText(row?.subdivision_code),
    externalId: normalizeText(row?.external_id),
  };
}

export function mapPublicHolidays(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map(normalizePublicHoliday)
    .filter(Boolean)
    .sort((left, right) => {
      const dateCompare = String(left.date || "").localeCompare(String(right.date || ""));
      if (dateCompare !== 0) return dateCompare;
      return String(left.name || "").localeCompare(String(right.name || ""), "de");
    });
}

export function normalizeDayComment(row) {
  const dateKey = toIsoDate(row?.date);
  const comment = normalizeText(row?.comment);
  if (!dateKey || !comment) return null;

  return {
    id: normalizeText(row?.id),
    dateKey,
    comment,
  };
}

export function mapDayComments(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map(normalizeDayComment)
    .filter(Boolean)
    .sort((left, right) => left.dateKey.localeCompare(right.dateKey));
}

export function normalizeAvailabilityEntry(row) {
  const id = normalizeText(row?.id ?? row?.UserAvailabilityID);
  const userId = normalizeText(row?.user_id ?? row?.userId ?? row?.UserID);
  const type = normalizeText(row?.type ?? row?.availability_type ?? row?.availabilityType).toLowerCase();
  const start = normalizeText(row?.start_at ?? row?.startAt);
  const end = normalizeText(row?.end_at ?? row?.endAt);
  if (!id || !userId || !["availability", "absence"].includes(type) || !start || !end) return null;

  return {
    id,
    userId,
    type,
    start,
    end,
    isFullDay: normalizeBoolean(row?.is_full_day ?? row?.isFullDay, false),
    comment: normalizeText(row?.comment),
    releaseStatus: normalizeReleaseStatus(
      row?.release_status ?? row?.releaseStatus,
      row?.is_released ?? row?.isReleased,
      "open"
    ),
  };
}

export function mapAvailabilityEntries(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeAvailabilityEntry).filter(Boolean);
}

/*
  Abwesenheiten der Verfügbarkeitsseite als eigene Einträge im Raster
  (availability_absence_list). Der Server liefert sie nur mit Zusatzmodul
  employee_absences, schon je Tag zerlegt und nur freigegebene.
*/
export function normalizeAvailabilityAbsence(row) {
  const id = normalizeText(row?.id);
  const userId = normalizeText(row?.user_id ?? row?.userId);
  const dateKey = toIsoDate(row?.date);
  const start = normalizeText(row?.start_at ?? row?.startAt);
  const end = normalizeText(row?.end_at ?? row?.endAt);
  if (!id || !userId || !dateKey || !start || !end) return null;

  return {
    id,
    userId,
    dateKey,
    start,
    end,
    isFullDay: normalizeBoolean(row?.is_full_day ?? row?.isFullDay, false),
    comment: normalizeText(row?.comment),
  };
}

export function mapAvailabilityAbsences(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeAvailabilityAbsence).filter(Boolean);
}

export function normalizeAvailabilityDefault(row) {
  const id = normalizeText(row?.id ?? row?.UserAvailabilityDefaultID);
  const userId = normalizeText(row?.user_id ?? row?.userId ?? row?.UserID);
  const type = normalizeText(
    row?.type ?? row?.time_record_type ?? row?.timeRecordType ?? row?.availability_type ?? row?.availabilityType
  ).toLowerCase();
  const weekday = Number(row?.weekday);
  const isActive = normalizeBoolean(row?.is_active ?? row?.isActive, true);
  if (
    !isActive ||
    !id ||
    !userId ||
    !["availability", "absence"].includes(type) ||
    !Number.isInteger(weekday) ||
    weekday < 0 ||
    weekday > 6
  ) {
    return null;
  }

  return {
    id,
    userId,
    type,
    weekday,
    startTime: normalizeText(row?.start_time ?? row?.startTime),
    endTime: normalizeText(row?.end_time ?? row?.endTime),
    isFullDay: normalizeBoolean(row?.is_full_day ?? row?.isFullDay, false),
    comment: normalizeText(row?.comment),
  };
}

export function mapAvailabilityDefaults(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeAvailabilityDefault).filter(Boolean);
}

export function normalizeAvailabilityMode(value, fallback = "absence") {
  const mode = normalizeText(value).toLowerCase();
  return ["availability", "absence"].includes(mode) ? mode : fallback;
}

export function mapUserAvailabilityModes(source = {}) {
  return Object.entries(source || {}).reduce((accumulator, [userId, mode]) => {
    const normalizedUserId = normalizeText(userId);
    if (!normalizedUserId) return accumulator;
    accumulator[normalizedUserId] = normalizeAvailabilityMode(mode, "");
    if (!accumulator[normalizedUserId]) delete accumulator[normalizedUserId];
    return accumulator;
  }, {});
}

export function normalizeUserHolidayType(row) {
  const id = normalizeText(row?.id ?? row?.UserHolidayTypeID);
  const name = normalizeText(row?.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    hoursPerDay: parseNumber(row?.hours_per_day ?? row?.hoursPerDay),
    isActive: normalizeBoolean(row?.is_active ?? row?.isActive, true),
    isSystemDefault: normalizeBoolean(row?.is_system_default ?? row?.isSystemDefault, false),
    startTime: normalizeText(row?.start_time ?? row?.startTime),
    endTime: normalizeText(row?.end_time ?? row?.endTime),
  };
}

export function mapUserHolidayTypes(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeUserHolidayType).filter(Boolean);
}

export function normalizeUserHoliday(row) {
  const id = normalizeText(row?.id ?? row?.UserHolidayID);
  const userId = normalizeText(row?.user_id ?? row?.UserID);
  const holidayTypeId = normalizeText(row?.holiday_type_id ?? row?.UserHolidayTypeID);
  const startDate = toIsoDate(row?.start_date ?? row?.startDate);
  const endDate = toIsoDate(row?.end_date ?? row?.endDate) || startDate;
  if (!id || !userId || !holidayTypeId || !startDate || !endDate) return null;

  return {
    id,
    userId,
    holidayTypeId,
    holidayTypeName: normalizeText(row?.holiday_type_name ?? row?.holidayTypeName) || "Ferien",
    startDate,
    endDate,
    hoursPerDay: parseNumber(row?.hours_per_day_snapshot ?? row?.hoursPerDaySnapshot),
    totalHours: parseNumber(row?.total_hours ?? row?.totalHours),
    plannedMinutesPerDay: parseOptionalNumber(
      row?.planned_minutes_per_day ?? row?.plannedMinutesPerDay
    ),
    approvalRequired: normalizeBoolean(row?.approval_required ?? row?.approvalRequired, false),
    currentApprovalRequestId: row?.current_approval_request_id ?? row?.currentApprovalRequestId ?? null,
    created: normalizeText(row?.created),
    released: normalizeText(row?.released),
  };
}

export function mapUserHolidays(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeUserHoliday).filter(Boolean);
}

export function normalizeDepartmentOption(row) {
  if (Array.isArray(row)) {
    const id = normalizeText(row?.[0]);
    const name = normalizeText(row?.[1] ?? row?.[0]);
    if (!name) return null;
    return {
      id: id || name,
      name,
      label: name,
      shortName: normalizeText(row?.[2]),
      color: "",
    };
  }

  if (row && typeof row === "object") {
    const id = normalizeText(row.id ?? row.DepartmentID);
    const name = normalizeText(row.name ?? row.label ?? row.department ?? row.title ?? row.id);
    if (!name) return null;
    return {
      id: id || name,
      name,
      label: name,
      shortName: normalizeText(row.shorthand_symbol ?? row.shortName ?? row.short_name),
      color: normalizeText(row.color_hex ?? row.colorHex),
    };
  }

  const name = normalizeText(row);
  return name ? { id: name, name, label: name, shortName: "", color: "" } : null;
}

export function mapDepartmentOptions(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeDepartmentOption).filter(Boolean);
}

function normalizeSchedulerBreak(row) {
  if (!row || typeof row !== "object") return null;
  const start = row.start_at || "";
  const end = row.end_at || "";

  return {
    id: String(row.id ?? `break-${start}-${end}`),
    type: normalizeText(row.break_type) || "fixed",
    start,
    end,
    startTime: formatTime(start),
    endTime: formatTime(end),
    durationMinutes: row.duration_minutes == null ? null : Number(row.duration_minutes),
  };
}

function mapSchedulerBreaks(rows) {
  return (Array.isArray(rows) ? rows : []).map(normalizeSchedulerBreak).filter(Boolean);
}

export function normalizeSchedulerEvent(event, employeesById, shiftOptionsById, colorMap = {}) {
  const employeeId = String(event?.user_id ?? "").trim();
  const employee = employeesById[employeeId];
  if (!employee || employee.isPlaceholder || parseNumber(employeeId) < 1) return null;

  const start = event?.start_at || "";
  const end = event?.end_at || "";
  const dateKey = toIsoDate(start);
  if (!dateKey) return null;

  const shiftId = String(event?.shift_id ?? "").trim();
  const entryType = normalizeText(event?.entry_type) || "shift";
  const shiftOption = shiftOptionsById[shiftId] || null;
  const rawDepartment = normalizeText(event?.department_name);
  const customDepartment = normalizeText(event?.custom_department_name ?? event?.customDepartmentName);
  const rawTitle = normalizeText(event?.title);
  const title =
    entryType === "absence"
      ? rawTitle || rawDepartment || "Absenz"
      : rawTitle || shiftOption?.name || rawDepartment || "Schicht";
  const department = rawDepartment || (entryType === "absence" ? title : entryType === "custom_shift" ? "" : "Schicht");
  const titleShort = normalizeText(event?.title_short) || shiftOption?.shortName || title;
  const departmentShort =
    normalizeText(event?.department_short_name) || shiftOption?.departmentShortName || department;
  const startTime = formatTime(start);
  const endTime = formatTime(end);
  const isAllDay = normalizeBoolean(event?.is_all_day ?? event?.isAllDay, false);
  const note = normalizeText(event?.note);
  const releaseStatus = normalizeReleaseStatus(
    event?.release_status ?? event?.releaseStatus,
    event?.is_released ?? event?.isReleased
  );
  const isReleased = releaseStatus === "released";
  /*
    Die Serverfarbe bildet die gewählte Farbzuteilung ab und hat deshalb
    Vorrang. Das ist besonders für individuelle Schichten wichtig: Sind sie
    einer Stammabteilung zugeordnet, liefert der Server im Abteilungsmodus
    deren Farbe. Grau bleibt der Rückfall für Einträge ohne passende
    Stammdatenfarbe, etwa eine vollständig individuelle Abteilung.
  */
  const selectedColor = normalizeText(event?.color);
  const color =
    selectedColor ||
    (isIndividualEntry({ entryType, customDepartmentName: customDepartment })
      ? INDIVIDUAL_COLOR
      : normalizeText(colorMap?.[`absence:${event?.absence_id}`]) ||
        normalizeText(colorMap?.[shiftId]) ||
        normalizeText(colorMap?.[employeeId]) ||
        normalizeText(colorMap?.[department]) ||
        FALLBACK_SHIFT_COLOR);

  return {
    id: String(event?.id ?? `${employeeId}-${dateKey}-${title}`),
    employeeId,
    employeeName: employee.name,
    // Kurzform für die Monatsansicht. Aus Vor- und Nachname gebaut statt aus
    // `name` zurückgesplittet — sonst würde "Dominik von Wyss" falsch geteilt.
    employeeShortName:
      formatEmployeeShortName(employee.firstName, employee.lastName) || shortenFullName(employee.name),
    entryType,
    absenceId: event?.absence_id == null ? "" : String(event.absence_id),
    absenceHoursPerDay: parseOptionalNumber(event?.absence_hours_per_day ?? event?.absenceHoursPerDay),
    department,
    departmentShortName: departmentShort,
    // Individuelle Abteilung: der Name gehört nur diesem Eintrag. `department`
    // trägt ihn bereits (der Server liefert ihn im selben Feld) – dieses Feld
    // sagt, dass dahinter kein Stammdatensatz steht.
    customDepartmentName: customDepartment,
    title,
    titleShort,
    note,
    shiftId,
    dateKey,
    start,
    end,
    created: normalizeText(event?.created),
    released: normalizeText(event?.released),
    releaseStatus,
    timeLabel: isAllDay ? "Ganztag" : [startTime, endTime].filter(Boolean).join(" - "),
    color,
    breaks: mapSchedulerBreaks(event?.breaks),
    // Vom Server berechnete geplante Minuten (Schicht: gesamt, Absenz: pro
    // Arbeitstag). Siehe databases/hours_resolution.py.
    plannedMinutes: parseOptionalNumber(event?.planned_minutes ?? event?.plannedMinutes),
    plannedMinutesPerDay: parseOptionalNumber(
      event?.planned_minutes_per_day ?? event?.plannedMinutesPerDay
    ),
    isAllDay,
    isDraft: !isReleased,
  };
}

function sortShiftsForGrid(shifts) {
  return shifts.sort((left, right) => {
    const dateCompare = String(left.start || "").localeCompare(String(right.start || ""));
    if (dateCompare !== 0) return dateCompare;
    return String(left.title || "").localeCompare(String(right.title || ""));
  });
}

function indexById(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((accumulator, row) => {
    if (row?.id) accumulator[row.id] = row;
    return accumulator;
  }, {});
}

/*
  Antwort von /entries — nur der zeitabhängige Teil eines beliebigen Zeitraums.

  Die Nachschlagetabellen kommen aus den bereits geladenen Stammdaten und nicht
  aus der Antwort: Personen, Schichtvorlagen und Farben ändern sich nicht mit
  dem Zeitraum, sie noch einmal mitzuschicken wäre nur Gewicht. shiftTemplates
  ist mapShiftOptions als Liste, taugt also unverändert als Vorlagen-Register.
*/
export function mapEntriesPayload(payload, { employees = [], shiftTemplates = [], colorMap = {} } = {}) {
  const employeesById = indexById(employees);
  const shiftOptionsById = indexById(shiftTemplates);

  return {
    shifts: sortShiftsForGrid(
      (Array.isArray(payload?.event_data_web) ? payload.event_data_web : [])
        .map((event) => normalizeSchedulerEvent(event, employeesById, shiftOptionsById, colorMap))
        .filter(Boolean)
    ),
    userHolidays: mapUserHolidays(payload?.user_holiday_list),
    publicHolidays: mapPublicHolidays(payload?.public_holidays),
    dayComments: mapDayComments(payload?.day_comment_list),
    period: {
      startDate: normalizeText(payload?.period?.start_date),
      endDate: normalizeText(payload?.period?.end_date),
    },
  };
}

export function mapSchedulerPayload(payload) {
  const colorMap = payload?.color_map || {};
  const employees = (Array.isArray(payload?.user_list) ? payload.user_list : [])
    .map(parseSchedulerUserRow)
    .filter((employee) => employee.id && !employee.isPlaceholder)
    .map((employee) => ({
      ...employee,
      color: normalizeText(colorMap?.[employee.id]) || FALLBACK_SHIFT_COLOR,
    }));

  const employeesById = employees.reduce((accumulator, employee) => {
    accumulator[employee.id] = employee;
    return accumulator;
  }, {});

  const shiftOptionsById = mapShiftOptions(payload?.shift_list);
  const shifts = (Array.isArray(payload?.event_data_web) ? payload.event_data_web : [])
    .map((event) => normalizeSchedulerEvent(event, employeesById, shiftOptionsById, payload?.color_map || {}))
    .filter(Boolean)
    .sort((left, right) => {
      const dateCompare = String(left.start || "").localeCompare(String(right.start || ""));
      if (dateCompare !== 0) return dateCompare;
      return String(left.title || "").localeCompare(String(right.title || ""));
    });

  return {
    employees,
    shifts,
    shiftTemplates: mapShiftTemplates(payload?.shift_list, colorMap),
    absenceOptions: mapAbsenceOptions(payload?.absence_list),
    // Die Archive stehen nur in den Antworten der Typenverwaltung, sonst leer.
    absenceArchive: mapAbsenceOptions(payload?.absence_archive_list),
    userHolidayTypes: mapUserHolidayTypes(payload?.user_holiday_type_list),
    userHolidayTypeArchive: mapUserHolidayTypes(payload?.user_holiday_type_archive_list),
    userHolidays: mapUserHolidays(payload?.user_holiday_list),
    departments: mapDepartmentOptions(payload?.department_list),
    publicHolidays: mapPublicHolidays(payload?.public_holidays),
    dayComments: mapDayComments(payload?.day_comment_list),
    availabilityEntries: mapAvailabilityEntries(payload?.user_availability_list),
    availabilityAbsences: mapAvailabilityAbsences(payload?.availability_absence_list),
    availabilityDefaults: mapAvailabilityDefaults(payload?.user_availability_default_list),
    meta: {
      accessLevel: payload?.access_level || "",
      companyLocationNames: (Array.isArray(payload?.company_locations) ? payload.company_locations : [])
        .map((row) => normalizeText(row?.name))
        .filter(Boolean),
      displayCalendar: payload?.display_calendar || "",
      shiftDisplayMode: normalizeShiftDisplayMode(payload?.shift_display_mode),
      colorMap,
      availabilityMode: normalizeAvailabilityMode(payload?.company_availability_mode),
      userAvailabilityModes: mapUserAvailabilityModes(payload?.user_availability_mode_by_user_id),
      workingDays: Number(payload?.working_days || 0),
      currentWeekNumber: Number(payload?.current_week_num || 0),
      // Mitarbeitenden-Ansicht, Web page.shiftplan/employee_entry_comments: Kommentar
      // der eigenen Einträge schreiben. Fehlt das Feld, ist es aus.
      canEditOwnEntryComments: normalizeBoolean(payload?.can_edit_own_entry_comments, false),
      // Zusatzmodul WhatsApp. Fehlt das Feld, gilt es als nicht gebucht.
      whatsappSubscription: normalizeBoolean(
        payload?.whatsapp_subscription ?? payload?.whatsappSubscription,
        false
      ),
      // Saison-Sonderfall Wiesnshop: Excel-Export läuft über den Server
      // (altes Format mit Bedarfsansicht) statt über den Client-Builder.
      wiesnshopExcelExport: normalizeBoolean(
        payload?.wiesnshop_excel_export ?? payload?.wiesnshopExcelExport,
        false
      ),
      // Standort, zu dem diese Daten gehören – nicht der Umschalterstand.
      locationName: normalizeText(payload?.location?.name),
    },
  };
}

export function normalizeDemandEntry(row) {
  const dateKey = toIsoDate(row?.date);
  const templateId = normalizeText(row?.template_id ?? row?.templateId);
  const requiredWorkers = Math.max(0, Math.round(parseNumber(row?.required_workers ?? row?.requiredWorkers)));
  if (!dateKey || !templateId) return null;

  return {
    id: String(row?.id ?? `${templateId}::${dateKey}`),
    dateKey,
    templateId,
    shiftId: normalizeText(row?.shift_id ?? row?.shiftId),
    departmentId: normalizeText(row?.department_id ?? row?.departmentId),
    requiredWorkers,
    shiftName: normalizeText(row?.shift_name ?? row?.shiftName),
    shiftShortName: normalizeText(row?.shift_shorthand ?? row?.shiftShortName),
    departmentName: normalizeText(row?.department_name ?? row?.departmentName),
    departmentShortName: normalizeText(row?.department_shorthand ?? row?.departmentShortName),
    startTime: normalizeText(row?.start_time ?? row?.startTime),
    endTime: normalizeText(row?.end_time ?? row?.endTime),
    color: normalizeText(row?.color_hex ?? row?.colorHex),
  };
}

export function mapDemandPayload(payload) {
  return (Array.isArray(payload?.demand_list) ? payload.demand_list : [])
    .map(normalizeDemandEntry)
    .filter(Boolean);
}

export function normalizeWorkloadEntry(row) {
  const userId = String(row?.user_id ?? row?.userId ?? "").trim();
  if (!userId) return null;

  const plannedHours = parseNumber(row?.planned_hours ?? row?.plannedHours);
  const targetHours = parseNumber(row?.target_hours ?? row?.targetHours);
  const vacationDays = parseNumber(row?.vacation_days ?? row?.vacationDays);
  const progress = getProgressState(plannedHours, targetHours);

  return {
    userId,
    plannedHours,
    targetHours,
    vacationDays,
    label: normalizeText(row?.label) || `${formatHours(plannedHours)} / ${formatHours(targetHours)}`,
    vacationLabel: normalizeText(row?.vacation_label ?? row?.vacationLabel) || formatVacationDays(vacationDays),
    tone: progress.tone,
    progressDegrees: progress.progressDegrees,
    overageDegrees: progress.overageDegrees,
    source: "backend",
  };
}

export function mapWorkloadPayload(payload) {
  const rows = payload?.workload_by_employee_id || payload?.workloadByEmployeeId || {};
  return Object.values(rows).reduce((accumulator, row) => {
    const entry = normalizeWorkloadEntry(row);
    if (entry) accumulator[entry.userId] = entry;
    return accumulator;
  }, {});
}

export function mapPeriodSummaryPayload(payload) {
  const summary = payload?.period_summary || payload?.periodSummary;
  if (!summary) return null;

  return normalizePeriodSummary({
    ...summary,
    source: "backend",
  });
}
